import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import { Avatar, Box, Chip, Typography } from '@mui/material';
import { localPoint } from '@visx/event';
import { Group } from '@visx/group';
import { Treemap } from '@visx/hierarchy';
import { useParentSize } from '@visx/responsive';
import { TooltipWithBounds, defaultStyles, useTooltip } from '@visx/tooltip';
import { hierarchy, treemapSquarify } from 'd3-hierarchy';
import React, { useMemo, useCallback } from 'react';
import { formatCount } from '../../../../utils/D3/helper';

const background = 'transparent';
const defaultMargin = { top: 10, left: 10, right: 10, bottom: 10 };
const SVG_HEIGHT = 400;

// Extracted static helpers outside the component to prevent memory reallocation on every tooltip hover
const getBGColor = (changeType, PctPortfolio) => {
  switch (changeType) {
    case 'SOLD OFF':
      return 'rgb(190, 0, 0)'; 
    case 'REDUCED':
      return 'rgb(115, 32, 32)'; 
    case 'UNCHANGED':
      return 'rgb(57, 57, 57)'; 
    case 'NEW':
      return 'rgb(28, 164, 28)'; 
    case 'ADDED':
      return 'rgb(49, 85, 49)'; 
    default:
      return (PctPortfolio || 0) >= 0 ? 'rgb(49, 85, 49)' : 'rgb(115, 32, 32)';
  }
};

const getStrokeColor = (changeType, PctPortfolio) => {
  switch (changeType) {
    case 'SOLD OFF':
      return 'rgb(153, 0, 0)'; 
    case 'REDUCED':
      return 'rgb(92, 25, 25)'; 
    case 'UNCHANGED':
      return 'rgb(46, 46, 46)'; 
    case 'NEW':
      return 'rgb(22, 132, 22)'; 
    case 'ADDED':
      return 'rgb(39, 68, 39)'; 
    default:
      return (PctPortfolio || 0) >= 0 ? 'rgb(39, 68, 39)' : 'rgb(92, 25, 25)';
  }
};

const getFontSize = (width, height) => {
  if (width < 40 || height < 40) return '0.5rem';
  if (width < 50 || height < 50) return '0.875rem';
  if (width < 65 || height < 65) return '1.2rem';
  if (width < 85 || height < 85) return '1.5rem';
  if (width < 130 || height < 130) return '1.7rem';
  if (width < 350 || height < 350) return '2rem';
  return '2.5rem';
};

const getTickerText = (ticker, width) => {
  if (!ticker) return '';
  if (width < 40) return `${ticker.slice(0, 1)}..`;
  if (width < 57) return `${ticker.slice(0, 2)}..`;
  return ticker;
};

const HeatMap = ({ data, stockTickers = [], margin = defaultMargin }) => {
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipOpen,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip();

  const { parentRef, width } = useParentSize({ debounceTime: 150 });
  
  const safeWidth = width || 0;
  const xMax = Math.max(0, safeWidth - margin.left - margin.right);
  const yMax = Math.max(0, SVG_HEIGHT - margin.top - margin.bottom);

  // Memoize the heavy D3 hierarchy calculation so it doesn't fire on every mouse move
  const root = useMemo(() => {
    if (!data || data.length === 0) return null;
    return hierarchy({ children: data })
      .sum((d) => Math.abs(d.PctPortfolio || 0))
      .sort((a, b) => (b.data.PctPortfolio || 0) - (a.data.PctPortfolio || 0));
  }, [data]);

  const isStockTicker = useCallback(
    (ticker) => stockTickers.some((stock) => stock.t === ticker),
    [stockTickers]
  );

  return (
    <div ref={parentRef} style={{ position: 'relative', width: '100%', minHeight: SVG_HEIGHT }}>
      {safeWidth > 0 && root && (
        <svg width={safeWidth} height={SVG_HEIGHT}>
          <rect width={safeWidth} height={SVG_HEIGHT} rx={14} fill={background} />
          <Treemap
            top={margin.top}
            root={root}
            size={[xMax, yMax]}
            tile={treemapSquarify}
            round={true}
          >
            {(treemap) => (
              <Group>
                {treemap.descendants().map((node, i) => {
                  const nodeWidth = Math.max(0, node.x1 - node.x0);
                  const nodeHeight = Math.max(0, node.y1 - node.y0);
                  const hasValidDimensions = nodeWidth > 10 && nodeHeight > 10;
                  const clickable = node.data && isStockTicker(node.data.ticker);

                  return (
                    <Group
                      key={node.data?.ticker || `node-${i}`}
                      top={node.y0 + margin.top}
                      left={node.x0 + margin.left}
                    >
                      {node.depth === 1 && (
                        <rect
                          width={nodeWidth}
                          height={nodeHeight}
                          fill={getBGColor(node.data.changeType, node.data.PctPortfolio)}
                          stroke={getStrokeColor(node.data.changeType, node.data.PctPortfolio)}
                          strokeWidth={1}
                          opacity={0.9}
                          onClick={() => {
                            if (clickable) {
                              window.open(`/stock/${node.data.ticker}`, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          onMouseMove={(event) => {
                            const coords = localPoint(event.target.ownerSVGElement, event);
                            if (coords) {
                              showTooltip({
                                // 15px offset added to keep tooltip glued near cursor
                                tooltipLeft: coords.x + 15,
                                tooltipTop: coords.y + 15,
                                tooltipData: node.data,
                              });
                            }
                          }}
                          onMouseLeave={hideTooltip}
                          style={{ cursor: clickable ? 'pointer' : 'default' }}
                        />
                      )}
                      {node.depth === 1 && hasValidDimensions && (
                        <text
                          dx={nodeWidth / 2}
                          dy={nodeHeight / 2}
                          fontSize={getFontSize(nodeWidth, nodeHeight)}
                          fontWeight="bold"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          fill="white"
                          style={{ pointerEvents: 'none' }}
                        >
                          {getTickerText(node.data.ticker, nodeWidth)}
                        </text>
                      )}
                    </Group>
                  );
                })}
              </Group>
            )}
          </Treemap>
        </svg>
      )}

      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            ...defaultStyles,
            backgroundColor: 'white',
            color: 'black',
            padding: '0px',
            fontSize: '12px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            borderRadius: '4px',
            zIndex: 1000,
            pointerEvents: 'none', // Prevents erratic jumping behavior
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', my: 1, ml: 1, mr: 2 }}>
            <Avatar
              itemProp="logo"
              alt={tooltipData.ticker || 'Logo'}
              variant="rounded"
              src={tooltipData.ticker ? `https://logo.grufity.com/${tooltipData.ticker}.png` : undefined}
              sx={{
                width: 47,
                height: 47,
                mr: 1,
                mt: 0.28,
                background: '#f8f9fa',
                p: 0.1,
                border: '1px solid rgb(218,220,224)',
                borderRadius: '5px',
                boxShadow: 0,
                color: '#174c86',
                '& img': { objectFit: 'contain', borderRadius: '4px' },
                display: { md: 'block', sm: 'block', xs: 'none' },
              }}
            >
              <ApartmentRoundedIcon sx={{ width: 47, height: 47, color: '#174ea63a' }} />
            </Avatar>
            <Box sx={{ minWidth: 157 }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  {tooltipData.ticker}
                </Typography>
                
                {!tooltipData.incPct && tooltipData.changeType === 'NEW' ? (
                  <Chip
                    size="small"
                    label="New"
                    sx={{ color: 'rgb(19,115,51)', background: 'rgb(230,244,234)', borderRadius: '12px', fontWeight: 600, fontSize: '.9rem' }}
                  />
                ) : (
                  tooltipData.incPct !== 0 && tooltipData.incPct != null && (
                    <Chip
                      size="small"
                      label={tooltipData.incPct > 0 ? `+${tooltipData.incPct}%` : `${tooltipData.incPct}%`}
                      sx={{
                        color: tooltipData.incPct > 0 ? 'rgb(19,115,51)' : 'rgb(165,14,14)',
                        background: tooltipData.incPct > 0 ? 'rgb(230,244,234)' : 'rgb(252,232,230)',
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: '.9rem',
                      }}
                    />
                  )
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '.875rem', textTransform: 'capitalize', mr: 3, color: '#555' }}>
                  {tooltipData.nameOfIssuer?.toLowerCase()}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '.875rem', textTransform: 'capitalize', color: '#555', mt: 0.6 }}>
                  {`$${formatCount(Math.abs(tooltipData.value || 0), true, 1, Math.sign(tooltipData.value || 0))}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </TooltipWithBounds>
      )}
    </div>
  );
};

export default HeatMap;